import React from 'react';
import {
  AutocompleteInput,
  FormDataConsumer,
  NumberInput,
  ReferenceInput,
  SelectInput,
  TextInput,
  required,
} from 'react-admin';
import { Button, FormHelperText } from '@mui/material';
import { useFormContext } from 'react-hook-form';
import AchievementVideoInput from './AchievementVideoInput';
import InlineSvgPreview from './components/InlineSvgPreview';
import {
  AchievementTypeId,
  triggerTypeChoices,
  typeChoices,
  getRecommendedTimesCopy,
} from './achievementConstants';

const TypeTriggerHelperText = () => {
  return (
    <FormHelperText component="p">
      Session, Week, and Prep Session badges work with the <strong>Times</strong> trigger so you can
      set thresholds. Forum, Profile, and Membership badges stay on <strong>Coded</strong> (the UI
      will block that combination) because they are provisioned directly in code.
    </FormHelperText>
  );
};

// Provisioned in code — see rest-api AchievementService (Forum/Profile) and
// utils/membershipAchievements.ts (Membership).
const DisallowedCodedTypes = [
  AchievementTypeId.Forum,
  AchievementTypeId.Profile,
  AchievementTypeId.Membership,
];

const AchievementTypeInput = () => (
  <FormDataConsumer>
    {({ formData, ...rest }) => {
      const triggerValue = formData?.triggerType;
      const triggerNumeric = Number(triggerValue);
      const triggerString =
        typeof triggerValue === 'string' ? triggerValue.trim().toLowerCase() : '';
      const isCoded = triggerNumeric === 1 || triggerString === 'coded';
      const filteredChoices = isCoded
        ? typeChoices.filter((choice) => !DisallowedCodedTypes.includes(Number(choice.id)))
        : typeChoices;

      return (
        <>
          <SelectInput
            source="type"
            label="Type"
            choices={filteredChoices}
            validate={required()}
            fullWidth
            {...rest}
          />
          {isCoded && (
            <FormHelperText component="p" error>
              Forum, Profile, and Membership badges are provisioned in code when trigger type is
              Coded. Please contact Engineering to modify them.
            </FormHelperText>
          )}
        </>
      );
    }}
  </FormDataConsumer>
);

const ProgramReferenceInputContent = ({ formData, ...rest }) => {
  const formContext = useFormContext();
  const showField = Number(formData?.type) === AchievementTypeId.WorkoutRating;
  const programId = formData?.programId;

  React.useEffect(() => {
    const hasProgramValue = typeof programId !== 'undefined' && programId !== null;

    if (!showField && hasProgramValue && formContext?.setValue) {
      formContext.setValue('programId', null);
    }
  }, [formContext, programId, showField]);

  if (!showField) {
    return null;
  }

  return (
    <>
      <ReferenceInput
        source="programId"
        reference="programs"
        resource="achievements"
        label="Program"
        perPage={25}
        sort={{ field: 'name', order: 'ASC' }}
        allowEmpty
        {...rest}
      >
        <AutocompleteInput
          optionText={(record) =>
            record?.name ? `${record.name} (#${record.id})` : `Program #${record?.id}`
          }
          optionValue="id"
          fullWidth
        />
      </ReferenceInput>
      <FormHelperText component="p">
        Program selection is only required for Workout Rating achievements.
      </FormHelperText>
    </>
  );
};

const ProgramReferenceInput = () => (
  <FormDataConsumer>{(props) => <ProgramReferenceInputContent {...props} />}</FormDataConsumer>
);

const SessionReferenceInput = () => (
  <FormDataConsumer>
    {({ ...rest }) => {
      const formatProgramLabel = (record) => {
        if (record?.program?.name) {
          return ` • ${record.program.name}`;
        }
        if (record?.programId) {
          return ` • Program #${record.programId}`;
        }
        return '';
      };

      return (
        <>
          <ReferenceInput
            source="sessionId"
            reference="sessions"
            resource="achievements"
            label="Target Session"
            perPage={25}
            sort={{ field: 'name', order: 'ASC' }}
            allowEmpty
            {...rest}
          >
            <AutocompleteInput
              optionText={(record) => {
                const programLabel = formatProgramLabel(record);
                return record?.name
                  ? `${record.name}${programLabel} (#${record.id})`
                  : `Session #${record?.id}`;
              }}
              optionValue="id"
              filterToQuery={(searchText) => ({ q: searchText })}
              fullWidth
            />
          </ReferenceInput>
          <FormHelperText component="p">
            Earned the first time this session is completed. Leave blank to keep global session
            thresholds.
          </FormHelperText>
        </>
      );
    }}
  </FormDataConsumer>
);

const TimesValueInputContent = ({ formData, ...rest }) => {
  const formContext = useFormContext();
  const triggerRaw = formData?.triggerType;

  const isTimes = Number(triggerRaw) === 2;
  const hasSessionTarget = Number(formData?.sessionId) > 0;

  const hasTimesValue = typeof formData?.timesValue !== 'undefined' && formData.timesValue !== null;

  React.useEffect(() => {
    if (!isTimes && hasTimesValue && formContext?.setValue) {
      formContext.setValue('timesValue', null);
    }
  }, [formContext, hasTimesValue, isTimes]);

  React.useEffect(() => {
    if (hasSessionTarget && formData?.timesValue !== 1 && formContext?.setValue) {
      formContext.setValue('timesValue', 1);
    }
  }, [formContext, formData?.timesValue, hasSessionTarget]);

  let helperCopy = 'Only required when Trigger Type is Times.';
  if (hasSessionTarget) {
    helperCopy = 'Locked to 1 for session-specific achievements (first completion).';
  }

  const recommendedCopy = isTimes && !hasSessionTarget ? getRecommendedTimesCopy(triggerRaw) : null;
  const helperText = (
    <>
      {helperCopy}
      {recommendedCopy ? (
        <>
          <br />
          {recommendedCopy}
        </>
      ) : null}
    </>
  );

  return (
    <NumberInput
      source="timesValue"
      label="Times Value"
      disabled={!isTimes || hasSessionTarget}
      helperText={helperText}
      validate={!hasSessionTarget && isTimes ? [required()] : []}
      fullWidth
      {...rest}
    />
  );
};

const TimesValueInput = () => (
  <FormDataConsumer>{(props) => <TimesValueInputContent {...props} />}</FormDataConsumer>
);

const InlineSvgPreviewField = () => (
  <FormDataConsumer>
    {({ formData }) => <InlineSvgPreview value={formData?.icon} />}
  </FormDataConsumer>
);

// Skeleton matching the PWA FacetBadge (pwa/src/components/Achievements/facet/):
// medallion + ring track in a 110 viewBox, glyph drawn inside the 100x100
// group. The `facet-ring-fill` circle picks up the celebration modal's
// ring-sweep animation. Solid tone + 60%-opacity secondary facets keep new
// icons in the badge family; replace the placeholder diamond with the glyph.
const FACET_ICON_TEMPLATE = `<svg viewBox="0 0 110 110" width="1em" height="1em">
  <circle cx="55" cy="55" r="47" fill="#FFFFFF" style="filter:drop-shadow(0 2px 6px rgba(33,38,45,0.12))"/>
  <circle cx="55" cy="55" r="51" fill="none" stroke="#F0F2F5" stroke-width="4"/>
  <circle class="facet-ring-fill" cx="55" cy="55" r="51" fill="none" stroke="#0EA0C3" stroke-width="4" stroke-linecap="round" pathLength="100" stroke-dasharray="100 100" transform="rotate(-90 55 55)"/>
  <g transform="translate(27 27) scale(0.56)">
    <path d="M50 6 L8 50 L50 50 Z" fill="#0EA0C3"/>
    <path d="M50 6 L92 50 L50 50 Z" fill="#0EA0C3" fill-opacity="0.6"/>
    <path d="M8 50 L50 94 L50 50 Z" fill="#0EA0C3" fill-opacity="0.6"/>
    <path d="M92 50 L50 94 L50 50 Z" fill="#0EA0C3"/>
  </g>
</svg>`;

const InsertFacetTemplateButton = () => {
  const { setValue, getValues } = useFormContext();

  const handleInsert = () => {
    const current = getValues('icon');
    if (current && !window.confirm('Replace the current SVG with the facet template?')) {
      return;
    }
    setValue('icon', FACET_ICON_TEMPLATE, { shouldDirty: true, shouldTouch: true });
  };

  return (
    <Button variant="outlined" size="small" sx={{ mb: 1 }} onClick={handleInsert}>
      Insert facet badge template
    </Button>
  );
};

const AchievementFormFields = () => (
  <>
    <TextInput source="name" validate={required()} fullWidth />
    <TextInput source="description" multiline validate={required()} fullWidth />
    <SelectInput
      source="triggerType"
      label="Trigger Type"
      choices={triggerTypeChoices}
      validate={required()}
      fullWidth
    />
    <TypeTriggerHelperText />
    <AchievementTypeInput />
    <ProgramReferenceInput />
    <SessionReferenceInput />
    <TimesValueInput />
    <TextInput
      source="icon"
      label="Achievement Icon (SVG)"
      multiline
      helperText="Paste inline SVG. Scripts and foreign objects are stripped automatically. Start from the facet template to match the badge system: keep the medallion and rings, and draw your glyph inside the inner group."
      fullWidth
    />
    <InsertFacetTemplateButton />
    <InlineSvgPreviewField />
    <AchievementVideoInput
      helperText="Choose an existing upload from Media → Videos. Upload new videos there."
      showPreview
    />
  </>
);

export default AchievementFormFields;
